import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Chart } from 'primereact/chart';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';

const toDateStr = (d) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
};

const diasDelMes = (fecha) => {
    const year = fecha.getFullYear();
    const month = fecha.getMonth();
    const ultimoDia = new Date(year, month + 1, 0).getDate();
    const dias = [];
    for (let d = 1; d <= ultimoDia; d++) dias.push(new Date(year, month, d));
    return dias;
};

const DashboardPortero = () => {
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio, seleccionarBarrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [resumenAccesos, setResumenAccesos] = useState([]);
    const [resumenPaquetes, setResumenPaquetes] = useState([]);

    // ─── Auto-selección de barrio (portero entra directo, sin pasar por TableBarrios) ──
    useEffect(() => {
        if (barrio?.id) return;
        const barriosPermitidos = JSON.parse(localStorage.getItem('barriosPermitidos') || '[]');
        if (barriosPermitidos.length === 0) return;
        axios.get(`${HOST_API}api/barrios/GetBarrios`, {
            params: { barrioId: barriosPermitidos[0], administracionId }
        }).then(({ data }) => {
            const lista = Array.isArray(data) ? data : [data];
            const b = lista[0];
            if (b) seleccionarBarrio(b.barrioId, b.nombreBarrio, b.cuit, b.direccion, b.razonSocial, b.numeroCuenta, b.cuentaBancaria, b.tipoCuentaBancaria, b.alias);
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchResumen = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const dias = diasDelMes(selectedDate);
            const fechaDesde = toDateStr(dias[0]);
            const fechaHasta = toDateStr(dias[dias.length - 1]);

            const [accesosRes, paquetesRes] = await Promise.all([
                axios.get(`${HOST_API}api/controlAcceso/GetResumenPorDia`, { params: { administracionId, barrioId: barrio.id, fechaDesde, fechaHasta } }),
                axios.get(`${HOST_API}api/paquetes/GetPaquetesPorDia`, { params: { administracionId, barrioId: barrio.id, fechaDesde, fechaHasta } }),
            ]);
            setResumenAccesos(accesosRes.data ?? []);
            setResumenPaquetes(paquetesRes.data ?? []);
        } catch (err) {
            console.error('Error al obtener el resumen del portero:', err);
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, administracionId, selectedDate]);

    useEffect(() => { fetchResumen(); }, [fetchResumen]);

    const dias = useMemo(() => diasDelMes(selectedDate), [selectedDate]);

    const { labels, ingresosPorDia, egresosPorDia, paquetesPorDia } = useMemo(() => {
        const accesosPorFecha = new Map(resumenAccesos.map(r => [toDateStr(new Date(r.fecha)), r]));
        const paquetesPorFecha = new Map(resumenPaquetes.map(r => [toDateStr(new Date(r.fecha)), r]));
        return {
            labels: dias.map(d => String(d.getDate())),
            ingresosPorDia: dias.map(d => accesosPorFecha.get(toDateStr(d))?.cantidadIngresos ?? 0),
            egresosPorDia: dias.map(d => accesosPorFecha.get(toDateStr(d))?.cantidadEgresos ?? 0),
            paquetesPorDia: dias.map(d => paquetesPorFecha.get(toDateStr(d))?.cantidad ?? 0),
        };
    }, [dias, resumenAccesos, resumenPaquetes]);

    const totalIngresos = ingresosPorDia.reduce((a, b) => a + b, 0);
    const totalEgresos = egresosPorDia.reduce((a, b) => a + b, 0);
    const totalPaquetes = paquetesPorDia.reduce((a, b) => a + b, 0);

    const chartAccesos = useMemo(() => ({
        labels,
        datasets: [
            { label: 'Ingresos', data: ingresosPorDia, backgroundColor: '#12b76a', borderRadius: 4 },
            { label: 'Egresos', data: egresosPorDia, backgroundColor: '#f04438', borderRadius: 4 },
        ],
    }), [labels, ingresosPorDia, egresosPorDia]);

    const chartPaquetes = useMemo(() => ({
        labels,
        datasets: [
            { label: 'Paquetes recibidos', data: paquetesPorDia, backgroundColor: '#1570ef', borderRadius: 4 },
        ],
    }), [labels, paquetesPorDia]);

    const optionsBase = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: {
            x: { grid: { display: false } },
            y: { beginAtZero: true, ticks: { precision: 0 } },
        },
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <h4 style={{ margin: 0 }}>Dashboard</h4>
                <Calendar
                    value={selectedDate}
                    view="month"
                    dateFormat="mm/yy"
                    onChange={(e) => setSelectedDate(e.value)}
                    showIcon
                    className="p-inputtext-sm"
                />
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                    <ProgressSpinner style={{ width: 56, height: 56 }} strokeWidth="4" />
                </div>
            ) : !barrio?.id ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                    No se pudo determinar tu barrio asignado.
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
                        <div className="stat-card comments" style={{ flex: '1 1 180px', minWidth: 180 }}>
                            <div className="stat-change increase" style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.4rem', color: '#344054' }}>Ingresos del mes</div>
                            <div className="stat-value" style={{ fontSize: '1.4rem', color: '#12b76a' }}>{totalIngresos}</div>
                        </div>
                        <div className="stat-card comments" style={{ flex: '1 1 180px', minWidth: 180 }}>
                            <div className="stat-change increase" style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.4rem', color: '#344054' }}>Egresos del mes</div>
                            <div className="stat-value" style={{ fontSize: '1.4rem', color: '#f04438' }}>{totalEgresos}</div>
                        </div>
                        <div className="stat-card comments" style={{ flex: '1 1 180px', minWidth: 180 }}>
                            <div className="stat-change increase" style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.4rem', color: '#344054' }}>Paquetes recibidos</div>
                            <div className="stat-value" style={{ fontSize: '1.4rem', color: '#1570ef' }}>{totalPaquetes}</div>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <div className="card">
                                <h5 className="mt-0">Ingresos y egresos por día</h5>
                                <Chart type="bar" data={chartAccesos} options={optionsBase} style={{ height: '320px' }} />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="card">
                                <h5 className="mt-0">Paquetes recibidos por día</h5>
                                <Chart type="bar" data={chartPaquetes} options={optionsBase} style={{ height: '320px' }} />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default DashboardPortero;
